import { ENVIRONMENT_HELPER } from "@/helpers/environment.js";
import store from '@/store.js'
import i18n from '@/helpers/i18n';
import { decodeJWT } from "@/helpers/auth-header.js";

export const IS_LOADING_STATUS = "loading";

// Flag to prevent multiple concurrent refresh attempts
let isRefreshing = false;
// Queue of requests waiting for token refresh
let refreshSubscribers = [];

export const API_HELPER = {
    abortController: new AbortController(),
    pathJoin,
    loggedApiCall,
    apiCall,
    refreshToken
};

function pathJoin(parts)
{
    return parts.map((part, i) => 
    {
        if (i === 0) {
          return part.toString().trim().replace(/[\/]*$/g, '')
        } else {
          return part.toString().trim().replace(/(^[\/]*|[\/]*$)/g, '')
        }
    }).filter(x=>x.length).join('/');
}

/* Handles 1) request cancellation 2) refresh the token if the user is not logged in 3) fetch url 4) error handler
   keepErrorBody: reject with the API's error body ({ errors, message }) instead of the generic message, so the caller can show it */
function loggedApiCall(url, queryParams, requestOptions, canBeAborted, keepErrorBody)
{
    // Validate parameters
    if(!url || !requestOptions)
    {
        throw("The necessary info for the api call was not provided!");
    }

    if(ENVIRONMENT_HELPER.isDev()) { console.log(requestOptions) }

    canBeAborted = (canBeAborted === undefined || canBeAborted === null) ? true : canBeAborted;

    // Validate if the user is logged in.
    let user = JSON.parse(localStorage.getItem('user'));
    if(!user || !user.data.accessToken)
    {
        throw("The user is not logged in or the current session is not valid!");
    }

    // Check if token is expired or will expire within 10 minutes
    var expiredTime = new Date(user.expired_time);
    var now = new Date();
    now.setMinutes(now.getMinutes() + 10);

    if(expiredTime < now)
    {
        // Token is expired or about to expire - refresh it
        return refreshToken(user)
            .then(newUser => {
                // Update the user reference with refreshed token
                user = newUser;
                // Continue with the original request using new token
                return executeApiCall(url, queryParams, requestOptions, canBeAborted, user, keepErrorBody);
            })
            .catch(refreshError => {
                if(ENVIRONMENT_HELPER.isDev()) { console.log("Token refresh failed:", refreshError); }
                store.dispatch('users/logout', null, { root: true });
                return Promise.reject("Session expired. Please login again.");
            });
    }

    // Token is still valid, proceed with request
    return executeApiCall(url, queryParams, requestOptions, canBeAborted, user, keepErrorBody);
}

/**
 * Refresh the JWT token using the refresh token
 * @param {Object} user - Current user object from localStorage
 * @returns {Promise<Object>} - Promise resolving to updated user object
 */
function refreshToken(user)
{
    // If already refreshing, wait for that refresh to complete
    if(isRefreshing)
    {
        return new Promise((resolve, reject) => {
            refreshSubscribers.push({ resolve, reject });
        });
    }

    isRefreshing = true;

    // Extract email from JWT token
    const decoded = decodeJWT(user.data.accessToken);
    const userEmail = decoded['http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress'] ||
                     decoded.email ||
                     decoded.sub ||
                     '';

    const refreshTokenValue = user.data.refreshToken;

    if(!refreshTokenValue)
    {
        isRefreshing = false;
        return Promise.reject("No refresh token available");
    }

    var refreshUrl = pathJoin([process.env.VUE_APP_API_ROOT, "Users", "refresh"]);

    const requestOptions = {
        method: 'POST',
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            token: refreshTokenValue,
            userEmail: userEmail
        })
    };

    return fetch(encodeURI(refreshUrl), requestOptions)
        .then(response => {
            if(ENVIRONMENT_HELPER.isDev()) { console.log("Refresh response:", response); }
            return response.text().then(text => {
                const data = text && JSON.parse(text);
                if(!response.ok)
                {
                    throw new Error(data.message || "Token refresh failed");
                }
                return data;
            });
        })
        .then(data => {
            // Update stored user with new tokens
            const updatedUser = {
                ...user,
                data: {
                    ...user.data,
                    accessToken: data.data.accessToken,
                    refreshToken: data.data.refreshToken
                },
                expired_time: data.expired_time
            };

            localStorage.setItem('user', JSON.stringify(updatedUser));

            // Notify all waiting requests
            refreshSubscribers.forEach(subscriber => subscriber.resolve(updatedUser));
            refreshSubscribers = [];
            isRefreshing = false;

            return updatedUser;
        })
        .catch(error => {
            // Notify all waiting requests of failure
            refreshSubscribers.forEach(subscriber => subscriber.reject(error));
            refreshSubscribers = [];
            isRefreshing = false;

            throw error;
        });
}

/**
 * Execute the actual API call
 * @param {string} url - API endpoint URL
 * @param {Object} queryParams - Query parameters
 * @param {Object} requestOptions - Fetch request options
 * @param {boolean} canBeAborted - Whether request can be aborted
 * @param {Object} user - User object with access token
 * @param {boolean} keepErrorBody - Whether to reject with the API's error body
 * @returns {Promise} - Promise resolving to API response
 */
function executeApiCall(url, queryParams, requestOptions, canBeAborted, user, keepErrorBody)
{
    // Ensure auth header uses the (potentially refreshed) token
    if(requestOptions.headers && requestOptions.headers['Authorization'])
    {
        requestOptions.headers['Authorization'] = 'Bearer ' + user.data.accessToken;
    }

    // Build query parameters
    var fullUrl = encodeURI(url);
    if(queryParams)
    {
        var firstParam = true;
        for(var key in queryParams) 
        {
            var value = queryParams[key];

            // Include boolean false values explicitly - convert to string for proper API binding
            if(value !== null && value !== undefined && value !== '')
            {
                // Convert boolean to string explicitly for proper API binding
                var stringValue = typeof value === 'boolean' ? value.toString() : value;
                var encodedValue = encodeURIComponent(stringValue);
                
                if(firstParam) {
                    fullUrl += "?";
                    firstParam = false;
                } else {
                    fullUrl += "&";
                }
                
                fullUrl += `${key}=${encodedValue}`;
            }
        }
    }


    // Complete the request options adding signal so that the request can be aborted.
    if(canBeAborted)
    {
        requestOptions.signal = API_HELPER.abortController.signal;
    }

    // Add current language to request (only if not already set)
    if(!requestOptions.headers) 
    {
        requestOptions.headers = {};
    }
    // Allow overriding Accept-Language by setting it in headers before calling
    if(!requestOptions.headers["Accept-Language"]) {
        requestOptions.headers["Accept-Language"] = i18n.locale;
    }

    if(ENVIRONMENT_HELPER.isDev()) { console.log(fullUrl) }

    return fetch(fullUrl, requestOptions)
    .then(response =>{
        if(ENVIRONMENT_HELPER.isDev()) { console.log(response) }
        return response.text().then(text => {
            const data = text && JSON.parse(text);
            if(ENVIRONMENT_HELPER.isDev()) { console.log(data) }
            if (!response.ok) {
                var errorMsg;
                switch(response.status)
                {
                    case 401:
                        {
                            errorMsg = "The token is not valid.";

                            store.dispatch('users/logout', null, { root: true });
                            break;
                        }
                    default:
                        {
                            errorMsg = data;
                            break;
                        }
                }

                return Promise.reject(errorMsg);
            }
    
            return data;
        });        
    })
    .catch((err) => 
    {
        if (err.name === 'AbortError') 
        {
            console.log("The request was aborted!");
        }
        else
        {
            if(keepErrorBody && err && typeof err === 'object' && !(err instanceof Error) && (err.errors || err.message))
            {
                return Promise.reject(err);
            }
            else if(err && err.Errors)
            {
                let errorArray = [];
                for (var i = 0; i < Object.keys(err.Errors).length; i++) {
                    let key = Object.keys(err.Errors)[i];
                    errorArray[key] = err.Errors[key];
                } 

                return Promise.reject(errorArray);
            }
            else
            {
                return Promise.reject("Something went wrong, please try again. If the problem persists contact the support.");
            }
        }
    });
}

/* Handles 1) request cancellation 2) fetch url 3) error handler */
function apiCall(url, queryParams, requestOptions, canBeAborted)
{
    // Validate parameters
    if(!url || !requestOptions)
    {
        throw("The necessary info for the api call was not provided!");
    }

    if(ENVIRONMENT_HELPER.isDev()) { console.log(requestOptions) }

    canBeAborted = (canBeAborted === undefined || canBeAborted === null) ? true : canBeAborted;

    // Build query parameters
    var fullUrl = encodeURI(url);
    if(queryParams)
    {
        var firstParam = true;
        for(var key in queryParams) 
        {
            var value = queryParams[key];

            // Include boolean false values explicitly - convert to string for proper API binding
            if(value !== null && value !== undefined && value !== '')
            {
                // Convert boolean to string explicitly for proper API binding
                var stringValue = typeof value === 'boolean' ? value.toString() : value;
                var encodedValue = encodeURIComponent(stringValue);
                
                if(firstParam) {
                    fullUrl += "?";
                    firstParam = false;
                } else {
                    fullUrl += "&";
                }
                
                fullUrl += `${key}=${encodedValue}`;
            }
        }
    }

    // Complete the request options adding signal so that the request can be aborted.
    if(canBeAborted)
    {
        requestOptions.signal = API_HELPER.abortController.signal;
    }

    if(ENVIRONMENT_HELPER.isDev()) { console.log(fullUrl) }

    return fetch(fullUrl, requestOptions)
    .then(response =>{
        if(ENVIRONMENT_HELPER.isDev()) { console.log(response) }
        return response.text().then(text => {
            const data = text && JSON.parse(text);
            if(ENVIRONMENT_HELPER.isDev()) { console.log(data) }
            if (!response.ok) {
                var errorMsg;

                if(typeof error != 'string')
                {
                    if(ENVIRONMENT_HELPER.isProd())
                    {
                        errorMsg = "Something went wrong, please try again. If the problem persists contact the support.";
                    }
                    else
                    {
                        errorMsg = error.toString();
                    }
                }
                else
                {
                    errorMsg = error;
                }
    
                return Promise.reject(errorMsg);
            }
    
            return data;
        });        
    })
    .catch((err) => 
    {
        if (err.name === 'AbortError') 
        {
            console.log("The request was aborted!");
        }
        else
        {
            var errorMsg;

            if(ENVIRONMENT_HELPER.isProd())
            {
                errorMsg = "Something went wrong, please try again. If the problem persists contact the support.";
            }
            else
            {
                errorMsg = err.toString();
            }
    
            return Promise.reject(errorMsg);
        }
    });
}